/**
 * Published snapshots — the Tuesday and Friday publishes of the Ad Dashboard,
 * newest first, as a vertical list inside the Snapshots drawer. Each entry
 * opens the stored HTML of that publish in the dashboard frame.
 */

import React from 'react';
import { Box, ButtonBase, Skeleton, Stack, Typography } from '@mui/material';
import { AdsSnapshot, formatSnapshotDate } from './api';

interface Props {
  snapshots: AdsSnapshot[] | null;
  error: string | null;
  selectedId: string | null;
  onSelect: (snapshot: AdsSnapshot) => void;
}

const SnapshotsPanel: React.FC<Props> = ({ snapshots, error, selectedId, onSelect }) => (
  <Box sx={{ width: 340, maxWidth: '92vw', p: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Published snapshots</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Each Tuesday and Friday run saves a frozen copy of the dashboard as it looked then. The live
      dashboard is always the current data.
    </Typography>
    <Stack spacing={1.25}>
      {snapshots === null && !error && [0, 1, 2].map((i) => (
        <Skeleton key={i} variant="rounded" height={132} />
      ))}
      {error && <Typography variant="body2" color="text.secondary">Could not load snapshots: {error}</Typography>}
      {snapshots && snapshots.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No snapshots yet. The first Tuesday or Friday publish will appear here.
        </Typography>
      )}
      {snapshots && snapshots.map((s) => (
        <ButtonBase
          key={s.id}
          onClick={() => onSelect(s)}
          focusRipple
          sx={{
            display: 'block', textAlign: 'left', borderRadius: 1.5, border: 2,
            borderColor: s.id === selectedId ? 'primary.main' : 'divider', overflow: 'hidden', bgcolor: 'background.paper',
          }}
        >
          {s.png_url ? (
            <Box component="img" src={s.png_url} alt="" loading="lazy" sx={{ width: '100%', height: 84, objectFit: 'cover', objectPosition: 'top', display: 'block', bgcolor: 'action.hover' }} />
          ) : (
            <Box sx={{ width: '100%', height: 84, bgcolor: 'action.hover' }} />
          )}
          <Box sx={{ px: 1.25, py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatSnapshotDate(s.date)}</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3 }}
              title={s.headline}
            >
              {s.headline}
            </Typography>
          </Box>
        </ButtonBase>
      ))}
    </Stack>
  </Box>
);

export default SnapshotsPanel;
